import { inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, startWith, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { SchedulerBaseItem } from '../../models/item';
import { AlertService } from '../../../../core/services/alert.service';
import { SchedulerAuthService } from '../../services/auth.service';
import { SchedulerContainerService } from '../../services/container.service';
import { orderByRank } from '../../../drag-drop/utils/order-by-rank';
import { FormControl, FormGroup } from '@angular/forms';
import { DragAndDropEvent } from '../../../drag-drop/types/event';
@Injectable()
export class SchedulerListService<Type extends SchedulerBaseItem>
  implements OnDestroy
{
  protected readonly alertService = inject(AlertService);
  protected readonly translateService = inject(TranslateService);
  protected readonly containerService = inject(SchedulerContainerService);
  protected readonly authService = inject(SchedulerAuthService);
  // subscription: Subscription;

  idList = 'scheduler-waiting-list';

  protected disableMovement = new BehaviorSubject(
    !this.authService.allowMovement()
  );
  disableMovement$ = this.disableMovement.asObservable().pipe(
    map((el) => {
      return this.authService.allowMovement() ? el : true;
    })
  );

  /**
   * @description this variable mainly means if you want to allow the list to interact with other lists
   */
  keepStatic = false;

  form = new FormGroup({
    search: new FormControl<string>(undefined),
  });
  private _filter = new BehaviorSubject<string>(undefined);

  filter$ = combineLatest([
    this.form.get('search').valueChanges.pipe(startWith('')),
    this.containerService.globalFilter$,
  ]).pipe(
    map(([search, search2]) => {
      if (search2) {
        return search2;
      }
      return search;
    }),
    tap((filter) => this._filter.next(filter))
  );

  protected _list = new BehaviorSubject<Type[]>([]);
  list$ = this._list.asObservable();
  set list(value: Type[]) {
    this._list.next(value);
  }
  filteredList$ = combineLatest([this.list$, this.filter$]).pipe(
    map(([list, filter]) => {
      return filter
        ? list.filter((item) => this.filterItem(item, filter))
        : list;
    })
  );

  filterItem: (item: Type, valueFilter: string) => boolean = undefined;

  constructor() {}

  ngOnDestroy(): void {
    this.containerService.removeList(this.idList);
    this.disableMovement.complete();
  }

  init(list: Type[], disableMovement = false): void {
    this.list = orderByRank(list);
    if (!this.keepStatic) {
      this.containerService.addNewList(this.idList, this._list);
    }
    this.disableMovement.next(disableMovement);
  }

  // onDropItem(dragDropEvent: DragAndDropEvent<SchedulerItem>): void {
  //   if (this.disableMovement.getValue()) {
  //     this.alertService.warning(
  //       this.translateService.instant('ERROR.DENY_SORTING_ACTIVE'),
  //       this.translateService.instant('ERROR.SORT_ACTIVE'),
  //       {
  //         timer: 4000,
  //         timerProgressBar: true,
  //         showCancelButton: false,
  //         confirmButtonText: this.translateService.instant('ALERT.OK'),
  //       }
  //     );
  //     return;
  //   }
  //   this.dragDropService.onDrop(dragDropEvent);
  // }

  onReplaceItem(item: Type): void {
    const list = this._list.getValue();
    const index = list.findIndex((el) => el.id === item.id);
    list[index] = item;
    this._list.next(orderByRank(list));
  }

  async validateForDragDrop(event: DragAndDropEvent<Type>): Promise<boolean> {
    if (
      event.previousContainer.id === event.container.id &&
      !this.authService.orderingItems()
    ) {
      return this.alertService
        .error(this.translateService.instant('ERROR.MAX_ORDER_LIST'))
        .then(() => false);
    }
    const filteredList = this._filter.getValue()
      ? this._list.value.filter((item) =>
          this.filterItem(item, this._filter.getValue())
        )
      : this._list.value;
    if (this._list.value.length !== filteredList.length) {
      this.alertService.error(
        this.translateService.instant('ERROR.DENY_SORTING_ACTIVE')
      );
      return false;
    }
    return true;
  }
}
